// LOCAL: placeholder section under StoreLayout — not implemented yet.

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Card,
  FloatingSave,
  SwitchInput,
  useToast,
} from 'corso-admin-ui';
import { commandStoreApi } from 'corso-types';
import { FormEventHandler } from 'react';
import { Controller, useForm } from 'react-hook-form';
import api from '~/api';
import { useRefetchStore, useStoreContext } from '~/providers/StoreProvider';
import { StorePage } from '../StorePage';
import LocationsCard from './LocationsCard';
import ShippingLinesCard from './ShippingLinesCard';

const settingSchema =
  commandStoreApi['/store/:storeId/ship-protect-settings'].put.request.body;

const formId = 'ship-protect-settings';

function BaseSettings() {
  const store = useStoreContext();
  const refetch = useRefetchStore();
  const toast = useToast();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm({
    values: store.shippingProtectionSettings,
    resolver: zodResolver(settingSchema),
  });

  const { mutateAsync: updateSettings } = useMutation({
    mutationFn: api.store(store.id).updateShipProtectSettings,
    onSuccess: () => {
      refetch();
      toast.show('Updated settings');
    },
    onError: () => toast.show('Failed to update settings'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => updateSettings(body))(event).catch(console.error);
  };

  return (
    <form
      id={formId}
      onSubmit={submitHandler}
      onReset={(event) => {
        event.preventDefault();
        reset();
      }}
    >
      <FloatingSave
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        form={formId}
      />

      <Card>
        <Card.Heading>Settings</Card.Heading>

        <Controller
          control={control}
          name="isUnprotectedOrderClaimPermitted"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              details="When enabled, unprotected orders can have claims created."
              id="is-unprotected-order-claim-permitted"
              label="Unprotected Order Claim Permitted"
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Card>
    </form>
  );
}

export default function ShippingProtectionPage() {
  return (
    <StorePage title="Shipping protection">
      <div className="flex flex-col gap-4">
        <Alert elevated variant="tip" heading="Looking for something?">
          All other settings for Shipping Protection are configured in the Corso
          Merchant UI.
        </Alert>
        <BaseSettings />
        <LocationsCard />
        <ShippingLinesCard />
      </div>
    </StorePage>
  );
}
